import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import './FlowLayout.css';

/* Mock data for demo/simulation */
const MOCK_LOCATIONS = [
  { value: '5004', label: 'San Francisco Global Entry EC', state: 'CA', address: '780 S Airport Blvd, San Francisco, CA 94128' },
  { value: '5006', label: 'Los Angeles International Airport', state: 'CA', address: '11099 S La Cienega Blvd, Los Angeles, CA 90045' },
  { value: '5140', label: 'San Diego Global Entry EC', state: 'CA', address: '2835 Pacific Hwy, San Diego, CA 92101' },
  { value: '5001', label: 'JFK International Airport', state: 'NY', address: 'JFK International Airport, Terminal 4, Jamaica, NY 11430' },
  { value: '5003', label: 'LaGuardia Airport', state: 'NY', address: 'LaGuardia Airport, Terminal B, East Elmhurst, NY 11371' },
  { value: '5007', label: 'Newark Liberty Airport', state: 'NJ', address: '3 Brewster Rd, Newark, NJ 07114' },
  { value: '5180', label: 'Houston Intercontinental Airport', state: 'TX', address: '3870 N Terminal Rd, Houston, TX 77032' },
  { value: '5181', label: 'Dallas-Fort Worth Airport', state: 'TX', address: '2400 Aviation Dr, DFW Airport, TX 75261' },
  { value: '5182', label: 'Austin-Bergstrom Airport', state: 'TX', address: '3600 Presidential Blvd, Austin, TX 78719' },
  { value: '5060', label: 'Chicago O\'Hare Airport', state: 'IL', address: '10000 W O\'Hare Ave, Terminal 5, Chicago, IL 60666' },
  { value: '5080', label: 'Miami International Airport', state: 'FL', address: '2100 NW 42nd Ave, Concourse J, Miami, FL 33142' },
  { value: '5081', label: 'Orlando International Airport', state: 'FL', address: '1 Jeff Fuqua Blvd, Orlando, FL 32827' },
  { value: '5082', label: 'Tampa International Airport', state: 'FL', address: '4100 George J Bean Pkwy, Tampa, FL 33607' },
  { value: '5100', label: 'Seattle-Tacoma Airport', state: 'WA', address: '17801 International Blvd, SeaTac, WA 98158' },
  { value: '5120', label: 'Denver International Airport', state: 'CO', address: '8500 Pena Blvd, Denver, CO 80249' },
  { value: '5160', label: 'Atlanta Hartsfield Airport', state: 'GA', address: '6000 N Terminal Pkwy, Atlanta, GA 30320' },
  { value: '5200', label: 'Boston Logan Airport', state: 'MA', address: '1 Harborside Dr, Terminal E, Boston, MA 02128' },
  { value: '5220', label: 'Detroit Metropolitan Airport', state: 'MI', address: '2565 World Gateway Pl, Detroit, MI 48242' },
  { value: '5240', label: 'Phoenix Sky Harbor Airport', state: 'AZ', address: '3400 E Sky Harbor Blvd, Terminal 4, Phoenix, AZ 85034' },
  { value: '5260', label: 'Minneapolis-St. Paul Airport', state: 'MN', address: '4300 Glumack Dr, Terminal 1, St. Paul, MN 55111' },
];

/* Group locations by state */
function groupByState(locations) {
  const stateMap = {};
  locations.forEach((loc) => {
    const state = loc.state && loc.state.trim() !== '' ? loc.state : 'Other';
    if (!stateMap[state]) stateMap[state] = [];
    stateMap[state].push(loc);
  });
  return stateMap;
}

function LocationDetails({ location }) {
  const address = location && location.address ? location.address : '';
  const hours = location && location.hours ? location.hours : 'Monday – Friday: 8:00 AM – 4:00 PM\nHours may vary by location.';
  const mapsUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address);
  return (
    <div className="info-card" style={{ border: 'none', padding: '16px 0 0', boxShadow: 'none' }}>
      <h3 className="info-card__title">Enrollment Center Hours</h3>
      <p className="info-card__text" style={{ whiteSpace: 'pre-line' }}>
        {hours}
      </p>
      {address && (
        <div>
          <h3 className="info-card__title" style={{ marginTop: '16px' }}>Address</h3>
          <p className="info-card__text">{address}</p>
          <a
            className="info-card__link"
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', marginTop: '8px' }}
          >
            View on Google Maps →
          </a>
        </div>
      )}
    </div>
  );
}

function LocationSelection() {
  const navigate = useNavigate();
  const [allLocations, setAllLocations] = useState([]);
  const [stateMap, setStateMap] = useState({});
  const [selectedState, setSelectedState] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINT}/api/locations/`);
        const locationOptions = response.data.map((location) => ({
          value: String(location.location_id),
          label: location.location_name,
          state: location.state,
          address: location.address,
          hours: location.hours,
          notes: location.notes
        }));
        setAllLocations(locationOptions);
        setStateMap(groupByState(locationOptions));
      } catch (error) {
        console.error('API unavailable, using mock data');
        setAllLocations(MOCK_LOCATIONS);
        setStateMap(groupByState(MOCK_LOCATIONS));
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const states = Object.keys(stateMap).sort();
  const locationsForState = selectedState ? stateMap[selectedState] || [] : [];

  const handleContinue = () => {
    if (!selectedLocation) return;
    var loc = allLocations.find(function(l) { return l.value === selectedLocation; });
    localStorage.setItem('locations', JSON.stringify([selectedLocation]));
    localStorage.setItem('selectedLocationName', loc ? loc.label : '');
    localStorage.setItem('selectedLocationAddress', loc ? (loc.address || '') : '');
    navigate('/calendar');
    window.scrollTo(0, 0);
  };

  return (
    <div className="flow-page">
      <Helmet>
        <title>Choose Location</title>
        <meta name="description" content="Choose your preferred Global Entry enrollment center location." />
      </Helmet>

      {/* Progress Bar */}
      <div className="progress-bar-container" role="progressbar" aria-valuenow={2} aria-valuemin={1} aria-valuemax={5} aria-label="Step 2 of 5: Choose Location">
        <div className="progress-bar-header">
          <button className="flow-back" onClick={() => { navigate('/get-started'); window.scrollTo(0, 0); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div className="progress-bar-label">Step 2 of 5: Choose Location</div>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: '40%' }} />
        </div>
      </div>

      {/* Split layout */}
      <div className="flow-layout">
        {/* Left column */}
        <div className="flow-left">
          <h1 className="flow-heading">
            Where would you like to go to your enrollment appointment?
          </h1>

          <div className="info-card info-card--subtle" style={{ marginTop: '48px' }}>
            <h3 className="info-card__title">Pro Tip</h3>
            <p className="info-card__text">
              Some enrollment centers are faster than others. Smaller airports and land border crossings
              often have shorter wait times and more available slots.
            </p>
          </div>
        </div>

        {/* Right column */}
        <div className="flow-right">
          <div className="panel-card">
            <div>
              <label className="flow-label" htmlFor="state-select">State</label>
              <select
                id="state-select"
                className={`flow-select ${!selectedState ? 'flow-select--placeholder' : ''}`}
                value={selectedState}
                onChange={(e) => {
                  setSelectedState(e.target.value);
                  setSelectedLocation('');
                }}
                disabled={loading}
              >
                <option value="" disabled hidden>
                  {loading ? 'Loading...' : 'Select a state'}
                </option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="flow-label" htmlFor="location-select">Enrollment Center</label>
              <select
                id="location-select"
                className={`flow-select ${!selectedLocation ? 'flow-select--placeholder' : ''}`}
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                disabled={!selectedState}
              >
                <option value="" disabled hidden>
                  {selectedState ? 'Select an enrollment center' : 'Select a state first'}
                </option>
                {locationsForState.map((loc) => (
                  <option key={loc.value} value={loc.value}>{loc.label}</option>
                ))}
              </select>
            </div>

            {selectedLocation && (
              <div className="location-highlight">
                <LocationDetails location={allLocations.find(l => l.value === selectedLocation)} />
              </div>
            )}

            <button
              className="flow-btn-primary"
              onClick={handleContinue}
              disabled={!selectedLocation}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationSelection;
